
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import UserImage from "@/components/UserImage.vue"
import { GroupedUserSchedules } from "@/types/timeManagement"
import { kebabCase } from "lodash"
import { User } from "@/types"
import { utils } from "@/mixins/utils"

interface FormattedCapacityValues {
	used: number,
	total: number,
	usedDisplay: string,
	totalDisplay: string
}

@Component({
	components: {
		"user-image": UserImage
	}
})
export default class TimeCalendarUserRows extends Vue {
	@Prop({ required: true })
	scheduleGroup!: GroupedUserSchedules

	@Prop({ default: () => [] })
	dateColumns!: any[]

	@Prop({ default: 1 })
	columnCount!: number

	@Prop({ default: () => [] })
	shiftPatterns!: string[]

	@Prop({ default: () => [] })
	shiftColors!: Record<string, string>

	@Prop({ default: false })
	showCapacity!: boolean

	@Prop({ default: false })
	allowEdit!: boolean

	@Prop({ type: Array, required: true })
	users!: User[]

	@Prop({ type: Boolean, default: true })
	showDisabledUserFormatting!: boolean

	kebabCase = kebabCase

	/**
	 * Raises set capacity event for group with provided details
	 */
	setCapacity (userId: string, date: string, isoFullDate: string, capacity: number | string) {
		this.$emit("setCapacity", {
			group: this.scheduleGroup.key,
			userId,
			date,
			isoFullDate,
			capacity
		})
	}

	private get disabledUserIds (): Set<string> {
		return new Set(this.users.filter(u => u.disabled).map(u => u.id))
	}

	/**
	 * @summary Calculates and returns formatted capacity values for a given day.
	 *
	 * This method calculates the following:
	 * - `used`: The used capacity (capacityTotal - capacityRemaining).
	 * - `total`: The total capacity, derived from capacityTotal.
	 * - `usedDisplay`: A string representation of the used capacity.
	 * - `totalDisplay`: A string representation of the total capacity.
	 *
	 * The display strings will be rounded to 2 decimals if doing so shows a clear difference.
	 * However, if the 2dp rounded values are identical (and used > total, triggering a warning)
	 * then the full precision values are returned.
	 *
	 * @param {any} day - The day object containing `capacityTotal` and `capacityRemaining` values.
	 * @returns {FormattedCapacityValues}
	 */
	private getFormattedCapacityValues (day: any): FormattedCapacityValues {
		const used = day.capacityTotal - day.capacityRemaining
		const total = day.capacityTotal

		const usedRounded = used % 1 === 0 ? used.toString() : parseFloat(used.toFixed(2)).toString()
		const totalRounded = total % 1 === 0 ? total.toString() : parseFloat(total.toFixed(2)).toString()

		let usedDisplay: string
		let totalDisplay: string

		// If used > total (warning state) but the 2dp rounded strings are identical,
		// then display the full numbers so the difference isn’t hidden.
		if (used > total && usedRounded === totalRounded) {
			usedDisplay = String(used)
			totalDisplay = String(total)
		} else {
			usedDisplay = usedRounded
			totalDisplay = totalRounded
		}

		return { used, total, usedDisplay, totalDisplay }
	}

	private getShiftCssStyle (assignmentDay: any): Record<string, string> {
		const title = assignmentDay?.shiftTitle || "none"
		const color = this.shiftColors?.[title] || ""
		return color ? { "--shift-color": color } : {}
	}
}
